import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';
// Header is provided by AuthenticatedRoute wrapper
import type { Campaign } from '../types/Campaign';
import MessageSequenceDisplay from '../components/campaigns/MessageSequenceDisplay';

const CampaignsList: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  
  // Table view state
  const [sortField, setSortField] = useState<'name' | 'status' | 'created_at' | 'submitted_at'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [leadSourceFilter, setLeadSourceFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  // View Messages state
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(null);
  const [expandedMessages, setExpandedMessages] = useState<any[] | null>(null);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Pause/Cancel state
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const navigate = useNavigate();

  // All campaigns now use the new wizard (Epoch 36+)
  const getEditRoute = (campaign: Campaign) => {
    return `/create-campaign-wizard?edit=${campaign.id}`;
  };

  // View Details functionality removed per user feedback

  const checkAuthAndFetchCampaigns = useCallback(async () => {
    try {
      // Try getUser first, fallback to getSession
      let user = null;
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        user = authUser;
      } catch (fetchError) {
        console.log('getUser failed, trying getSession:', fetchError);
        const { data: { session } } = await supabase.auth.getSession();
        user = session?.user;
      }

      if (!user) {
        navigate('/login');
        return;
      }

      await fetchCampaigns(user.id);
    } catch (err) {
      console.error('Auth check failed:', err);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuthAndFetchCampaigns();
  }, [checkAuthAndFetchCampaigns]);

  const fetchCampaigns = async (userId: string) => {
    try {
      setLoading(true);

      // Fetch campaigns (no nested join — Epoch 75: separate query for preset_filter_requests)
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const campaignIds = (data || []).map(c => c.id);

      // Epoch 75: Separate query for preset_filter_requests to avoid nested join RLS issues
      let filterRequestMap: Record<string, string> = {};
      if (campaignIds.length > 0) {
        const { data: filterData } = await supabase
          .from('preset_filter_requests')
          .select('campaign_id, request_name')
          .in('campaign_id', campaignIds);
        (filterData || []).forEach((r: any) => {
          if (r.request_name) filterRequestMap[r.campaign_id] = r.request_name;
        });
      }

      // Collect unique CSR IDs
      const csrIds = [...new Set((data || [])
        .filter(c => c.assigned_csr)
        .map(c => c.assigned_csr))];

      let csrMap: Record<string, any> = {};

      // Fetch CSR details in batch if there are any CSR IDs
      if (csrIds.length > 0) {
        const { data: csrData, error: csrError } = await supabase
          .from('users')
          .select('id, full_name, email')
          .in('id', csrIds);

        if (csrError) {
          console.log('Error fetching CSR data:', csrError);
        } else if (csrData) {
          csrData.forEach(csr => {
            csrMap[csr.id] = csr;
          });
        }
      }

      // Merge CSR names and filter request names into campaigns
      const campaignsWithCSR = (data || []).map(campaign => {
        const csrName = campaign.assigned_csr && csrMap[campaign.assigned_csr]
          ? csrMap[campaign.assigned_csr].full_name || csrMap[campaign.assigned_csr].email || 'CSR Assigned'
          : campaign.assigned_csr ? 'CSR Assigned' : undefined;
        return {
          ...campaign,
          ...(csrName ? { assigned_csr_name: csrName } : {}),
          // Epoch 75: inject as array shape so getLeadSourceDisplay() still works
          preset_filter_requests: filterRequestMap[campaign.id]
            ? [{ request_name: filterRequestMap[campaign.id] }]
            : [],
        };
      });

      setCampaigns(campaignsWithCSR || []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (typeof err === 'object' && err !== null ? JSON.stringify(err) : 'Failed to load campaigns');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: Campaign['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'preparing':
        return 'bg-yellow-100 text-yellow-800';
      case 'running':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'paused':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLeadSourceDisplay = (campaign: any) => {
    // Check both lead_acquisition_method and lead_source_type fields
    const leadMethod = campaign.lead_acquisition_method || campaign.lead_source_type;
    const targetType = campaign.target_audience_type;
    
    // Debug log to see what we're getting
    if (campaign.name && campaign.name.includes('JD')) {
      console.log(`Campaign ${campaign.name} - lead_acquisition_method: ${campaign.lead_acquisition_method}, lead_source_type: ${campaign.lead_source_type}, target_audience_type: ${targetType}`);
    }
    
    // For Target Lead Lists and Previous Target Lead Lists, show the request name if available
    if (leadMethod === 'preset_filters' || leadMethod === 'preset_filter' || leadMethod === 'past_list') {
      const requestName = campaign.preset_filter_requests?.[0]?.request_name;
      if (requestName) {
        const prefix = (leadMethod === 'past_list') ? 'Previous List' : 'Target Leads';
        return `${prefix}: ${requestName}`;
      }
      return (leadMethod === 'past_list') ? 'Previous Target Lead List' : 'Target Lead List';
    } else if (leadMethod === 'csv_upload') {
      return 'CSV Upload';
    } else if (leadMethod === 'lead_generator') {
      return 'Lead Generator';
    } else if (targetType === 'preset_filters' || targetType === 'preset_filter') {
      // Legacy support - check for preset filter requests
      const requestName = campaign.preset_filter_requests?.[0]?.request_name;
      if (requestName) {
        return `Target Leads: ${requestName}`;
      }
      return 'Target Lead List';
    } else if (targetType === 'past_list') {
      // Legacy support for past list
      const requestName = campaign.preset_filter_requests?.[0]?.request_name;
      if (requestName) {
        return `Previous List: ${requestName}`;
      }
      return 'Previous Target Lead List';
    } else if (targetType === 'csv_upload') {
      return 'CSV Upload'; // Legacy support
    }
    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filtering and sorting logic
  const filteredAndSortedCampaigns = useMemo(() => {
    let filtered = [...campaigns];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Lead source filter
    if (leadSourceFilter !== 'all') {
      filtered = filtered.filter(c => {
        const leadSource = getLeadSourceDisplay(c);
        if (leadSourceFilter === 'target_leads') {
          return leadSource === 'Target Lead List';
        } else if (leadSourceFilter === 'previous_list') {
          return leadSource === 'Previous Target Lead List';
        } else if (leadSourceFilter === 'csv_upload') {
          return leadSource === 'CSV Upload';
        }
        return false;
      });
    }

    // Date range filter
    if (dateRange.start) {
      filtered = filtered.filter(c => 
        new Date(c.created_at) >= new Date(dateRange.start)
      );
    }
    if (dateRange.end) {
      filtered = filtered.filter(c => 
        new Date(c.created_at) <= new Date(dateRange.end + 'T23:59:59')
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField as keyof Campaign];
      let bVal = b[sortField as keyof Campaign];
      
      if (!aVal) return sortDirection === 'asc' ? 1 : -1;
      if (!bVal) return sortDirection === 'asc' ? -1 : 1;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return 0;
    });

    return filtered;
  }, [campaigns, searchTerm, statusFilter, leadSourceFilter, dateRange, sortField, sortDirection]);

  // Stats calculation
  const stats = useMemo(() => {
    return {
      total: campaigns.length,
      draft: campaigns.filter(c => c.status === 'draft').length,
      submitted: campaigns.filter(c => c.status === 'submitted').length,
      preparing: campaigns.filter(c => c.status === 'preparing').length,
      running: campaigns.filter(c => c.status === 'running').length,
      completed: campaigns.filter(c => c.status === 'completed').length,
      paused: campaigns.filter(c => c.status === 'paused').length,
      cancelled: campaigns.filter(c => c.status === 'cancelled').length
    };
  }, [campaigns]);

  const handleSort = (field: typeof sortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const submitCampaignForReview = async (campaign: Campaign) => {
    if (!confirm(`Submit "${campaign.name}" for CSR review? Once submitted, you'll need CSR approval to make changes.`)) {
      return;
    }

    setSubmittingId(campaign.id);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Update campaign status to submitted
      const { error } = await supabase
        .from('campaigns')
        .update({
          status: 'submitted',
          submitted_at: new Date().toISOString()
        })
        .eq('id', campaign.id);

      if (error) {
        console.error('Error submitting campaign:', error);
        alert('Failed to submit campaign. Please try again.');
        return;
      }

      // Get user's full name and assigned CSR for notification
      const { data: userData } = await supabase
        .from('users')
        .select('full_name, assigned_csr')
        .eq('id', session.user.id)
        .single();

      // Create notification for assigned CSR if they exist
      if (userData?.assigned_csr) {
        await supabase
          .from('notifications')
          .insert({
            user_id: userData.assigned_csr,
            type: 'campaign_submitted',
            title: 'Campaign Submitted for Review',
            message: `${userData.full_name || 'A user'} submitted "${campaign.name}" for approval`,
            data: { 
              campaign_id: campaign.id,
              user_id: session.user.id,
              campaign_name: campaign.name
            }
          });
      }

      // Refresh campaigns list
      await checkAuthAndFetchCampaigns();
      
      alert(`Campaign "${campaign.name}" submitted for review! Your CSR will be notified.`);
    } catch (error) {
      console.error('Error in submitCampaignForReview:', error);
      alert('Failed to submit campaign. Please try again.');
    } finally {
      setSubmittingId(null);
    }
  };

  const toggleMessages = async (campaignId: string) => {
    if (expandedCampaignId === campaignId) {
      setExpandedCampaignId(null);
      setExpandedMessages(null);
      return;
    }

    setMessagesLoading(true);
    setExpandedCampaignId(campaignId);

    try {
      // Messages are stored as JSONB in campaign_sequences table, linked via sequence_id
      const { data, error } = await supabase
        .from('campaigns')
        .select('campaign_sequences!sequence_id(messages)')
        .eq('id', campaignId)
        .single();

      if (error) throw error;
      const messages = (data as any)?.campaign_sequences?.messages || [];
      setExpandedMessages(messages);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setExpandedMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const notifyCSR = async (campaign: Campaign, type: string, title: string, message: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Use campaign.assigned_csr (campaign-level assignment) as primary
      // This is the correct CSR for this campaign, set via companies.assigned_csr_id
      const csrId = campaign.assigned_csr;
      if (!csrId) return;

      const { data: userData } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', session.user.id)
        .single();

      await supabase
        .from('notifications')
        .insert({
          user_id: csrId,
          type,
          title,
          message: `${userData?.full_name || 'A user'} ${message}`,
          data: {
            campaign_id: campaign.id,
            user_id: session.user.id,
            campaign_name: campaign.name
          }
        });
    } catch (err) {
      // Non-blocking - don't fail the action if notification fails
      console.error('Error sending CSR notification:', err);
    }
  };

  const pauseCampaign = async (campaign: Campaign) => {
    if (!confirm(`Pause "${campaign.name}"? You can resume it later.`)) return;

    setActionLoading(campaign.id);
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ status: 'paused' })
        .eq('id', campaign.id);

      if (error) throw error;
      await notifyCSR(campaign, 'campaign_paused', 'Campaign Paused', `paused campaign "${campaign.name}"`);
      await checkAuthAndFetchCampaigns();
    } catch (err) {
      console.error('Error pausing campaign:', err);
      alert('Failed to pause campaign. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const resumeCampaign = async (campaign: Campaign) => {
    setActionLoading(campaign.id);
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ status: 'running' })
        .eq('id', campaign.id);

      if (error) throw error;
      await notifyCSR(campaign, 'campaign_resumed', 'Campaign Resumed', `resumed campaign "${campaign.name}"`);
      await checkAuthAndFetchCampaigns();
    } catch (err) {
      console.error('Error resuming campaign:', err);
      alert('Failed to resume campaign. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const cancelCampaign = async (campaign: Campaign) => {
    if (!confirm(`Cancel "${campaign.name}"? This action cannot be undone.`)) return;

    setActionLoading(campaign.id);
    try {
      const { error } = await supabase
        .from('campaigns')
        .update({ status: 'cancelled' })
        .eq('id', campaign.id);

      if (error) throw error;
      await notifyCSR(campaign, 'campaign_cancelled', 'Campaign Cancelled', `cancelled campaign "${campaign.name}"`);
      await checkAuthAndFetchCampaigns();
    } catch (err) {
      console.error('Error cancelling campaign:', err);
      alert('Failed to cancel campaign. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading campaigns...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Campaigns</h1>
              <p className="text-gray-600">View and manage all your marketing campaigns</p>
            </div>
            <div className="flex items-center space-x-3">
              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'table' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Table
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'cards' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Cards
                </button>
              </div>
              <button
                onClick={() => navigate('/create-campaign-wizard')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Drafts</p>
            <p className="text-2xl font-bold text-gray-700">{stats.draft}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-yellow-600">Under Review</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.submitted}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-blue-600">Preparing</p>
            <p className="text-2xl font-bold text-blue-600">{stats.preparing}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-green-600">Active</p>
            <p className="text-2xl font-bold text-green-600">{stats.running}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-orange-600">Paused</p>
            <p className="text-2xl font-bold text-orange-600">{stats.paused}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-red-600">Cancelled</p>
            <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <p className="text-sm text-purple-600">Completed</p>
            <p className="text-2xl font-bold text-purple-600">{stats.completed}</p>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Campaign name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Under Review</option>
                <option value="preparing">Preparing</option>
                <option value="running">Running</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            
            {/* Lead Source Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
              <select
                value={leadSourceFilter}
                onChange={(e) => setLeadSourceFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Sources</option>
                <option value="target_leads">Target Lead List</option>
                <option value="previous_list">Previous Target List</option>
                <option value="csv_upload">CSV Upload</option>
              </select>
            </div>
            
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          {/* Clear Filters */}
          {(searchTerm || statusFilter !== 'all' || leadSourceFilter !== 'all' || dateRange.start || dateRange.end) && (
            <div className="mt-3">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setLeadSourceFilter('all');
                  setDateRange({ start: '', end: '' });
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {campaigns.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No campaigns yet</h3>
            <p className="text-gray-600 mb-6">
              Get started by creating your first marketing campaign.
            </p>
            <button
              onClick={() => navigate('/create-campaign-wizard')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Your First Campaign
            </button>
          </div>
        ) : viewMode === 'table' ? (
          // Table View
          <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Campaign Name
                        {sortField === 'name' && (
                          <svg className={`ml-1 w-3 h-3 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {sortField === 'status' && (
                          <svg className={`ml-1 w-3 h-3 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead Source
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center">
                        Created
                        {sortField === 'created_at' && (
                          <svg className={`ml-1 w-3 h-3 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </th>
                    {/* Assigned CSR column hidden - not useful for users
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned CSR
                    </th>
                    */}
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedCampaigns.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        No campaigns match your filters
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedCampaigns.map((campaign) => (
                      <React.Fragment key={campaign.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                            {/* Epoch 75: Show target list name as subtitle */}
                            {(() => {
                              const leadMethod = campaign.lead_acquisition_method || campaign.lead_source_type;
                              const targetType = campaign.target_audience_type;
                              const isTargetList =
                                leadMethod === 'preset_filters' || leadMethod === 'preset_filter' || leadMethod === 'past_list' ||
                                targetType === 'preset_filters' || targetType === 'preset_filter' || targetType === 'past_list';
                              const requestName = (campaign as any).preset_filter_requests?.[0]?.request_name;
                              if (isTargetList && requestName) {
                                return <div className="text-xs text-gray-400 mt-0.5">📋 {requestName}</div>;
                              }
                              return null;
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {getLeadSourceDisplay(campaign) || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(campaign.created_at)}</div>
                          </td>
                          {/* Assigned CSR column hidden - not useful for users
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {campaign.assigned_csr ? ((campaign as any).assigned_csr_name || 'CSR Assigned') : '-'}
                            </div>
                          </td>
                          */}
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              {campaign.status === 'draft' && (
                                <>
                                  <button
                                    onClick={() => navigate(getEditRoute(campaign))}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => submitCampaignForReview(campaign)}
                                    disabled={submittingId === campaign.id}
                                    className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                  >
                                    {submittingId === campaign.id ? 'Submitting...' : 'Submit'}
                                  </button>
                                </>
                              )}
                              {campaign.status === 'submitted' && (
                                <span className="text-yellow-600">Under Review</span>
                              )}
                              {campaign.status === 'preparing' && (
                                <span className="text-blue-600">Preparing</span>
                              )}
                              {campaign.status === 'running' && (
                                <>
                                  <button
                                    onClick={() => pauseCampaign(campaign)}
                                    disabled={actionLoading === campaign.id}
                                    className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                                  >
                                    {actionLoading === campaign.id ? '...' : 'Pause'}
                                  </button>
                                  <button
                                    onClick={() => cancelCampaign(campaign)}
                                    disabled={actionLoading === campaign.id}
                                    className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                              {campaign.status === 'paused' && (
                                <>
                                  <button
                                    onClick={() => resumeCampaign(campaign)}
                                    disabled={actionLoading === campaign.id}
                                    className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                  >
                                    {actionLoading === campaign.id ? '...' : 'Resume'}
                                  </button>
                                  <button
                                    onClick={() => cancelCampaign(campaign)}
                                    disabled={actionLoading === campaign.id}
                                    className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                              {campaign.status === 'completed' && (
                                <span className="text-purple-600">Complete</span>
                              )}
                              {campaign.status === 'cancelled' && (
                                <span className="text-red-600">Cancelled</span>
                              )}
                              {campaign.status !== 'draft' && (
                                <button
                                  onClick={() => toggleMessages(campaign.id)}
                                  className={`ml-2 text-sm px-2 py-1 rounded border transition-colors ${
                                    expandedCampaignId === campaign.id
                                      ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                                      : 'text-indigo-600 hover:text-indigo-900 border-indigo-200 hover:bg-indigo-50'
                                  }`}
                                >
                                  {expandedCampaignId === campaign.id ? 'Hide Messages' : 'View Messages'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedCampaignId === campaign.id && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 bg-gray-50">
                              {messagesLoading ? (
                                <div className="flex items-center justify-center py-4">
                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                  <span className="ml-2 text-sm text-gray-600">Loading messages...</span>
                                </div>
                              ) : expandedMessages && expandedMessages.length > 0 ? (
                                <MessageSequenceDisplay
                                  messages={expandedMessages}
                                  title={`Messages for "${campaign.name}"`}
                                  showBackground={false}
                                />
                              ) : (
                                <p className="text-sm text-gray-500 text-center py-4">No messages found for this campaign.</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // Card View
          <div className="bg-white shadow-sm rounded-lg border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {filteredAndSortedCampaigns.length} Campaign{filteredAndSortedCampaigns.length !== 1 ? 's' : ''}
                {filteredAndSortedCampaigns.length !== campaigns.length && (
                  <span className="text-sm text-gray-500 ml-2">
                    (filtered from {campaigns.length} total)
                  </span>
                )}
              </h3>
            </div>
            
            <div className="divide-y divide-gray-200">
              {filteredAndSortedCampaigns.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  No campaigns match your filters
                </div>
              ) : (
                filteredAndSortedCampaigns.map((campaign) => (
                <div key={campaign.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-lg font-medium text-gray-900">{campaign.name}</h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </div>

                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a4 4 0 118 0v4m-4 0V3m0 0V0m0 3H8m8 0h4m-4 0a4 4 0 110 8h-4V7z" />
                          </svg>
                          Created {formatDate(campaign.created_at)}
                        </div>

                        {(campaign.lead_acquisition_method || campaign.target_audience_type) && getLeadSourceDisplay(campaign) && (
                          <div className="flex items-center">
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857M7 20H2a3 3 0 005.356-1.857M15 11a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zm-10 0a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {getLeadSourceDisplay(campaign)}
                          </div>
                        )}

                        {/* Assigned CSR hidden in card view - not useful for users
                        {campaign.assigned_csr && (
                          <div className="flex items-center">
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {(campaign as any).assigned_csr_name || 'CSR Assigned'}
                          </div>
                        )}
                        */}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      {campaign.status === 'draft' && (
                        <>
                          <button
                            onClick={() => navigate(getEditRoute(campaign))}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm px-3 py-1 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                          >
                            Edit Campaign
                          </button>
                          <button
                            onClick={() => submitCampaignForReview(campaign)}
                            disabled={submittingId === campaign.id}
                            className="text-green-600 hover:text-green-800 font-medium text-sm px-3 py-1 border border-green-300 rounded hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {submittingId === campaign.id ? 'Submitting...' : 'Submit for Review'}
                          </button>
                        </>
                      )}

                      {campaign.status === 'submitted' && (
                        <span className="text-yellow-600 text-sm font-medium">Awaiting CSR Review</span>
                      )}

                      {campaign.status === 'preparing' && (
                        <span className="text-blue-600 text-sm font-medium">Preparing for Launch</span>
                      )}

                      {campaign.status === 'running' && (
                        <>
                          <button
                            onClick={() => pauseCampaign(campaign)}
                            disabled={actionLoading === campaign.id}
                            className="text-orange-600 hover:text-orange-800 font-medium text-sm px-3 py-1 border border-orange-300 rounded hover:bg-orange-50 transition-colors disabled:opacity-50"
                          >
                            {actionLoading === campaign.id ? '...' : 'Pause'}
                          </button>
                          <button
                            onClick={() => cancelCampaign(campaign)}
                            disabled={actionLoading === campaign.id}
                            className="text-red-600 hover:text-red-800 font-medium text-sm px-3 py-1 border border-red-300 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </>
                      )}

                      {campaign.status === 'paused' && (
                        <>
                          <button
                            onClick={() => resumeCampaign(campaign)}
                            disabled={actionLoading === campaign.id}
                            className="text-green-600 hover:text-green-800 font-medium text-sm px-3 py-1 border border-green-300 rounded hover:bg-green-50 transition-colors disabled:opacity-50"
                          >
                            {actionLoading === campaign.id ? '...' : 'Resume'}
                          </button>
                          <button
                            onClick={() => cancelCampaign(campaign)}
                            disabled={actionLoading === campaign.id}
                            className="text-red-600 hover:text-red-800 font-medium text-sm px-3 py-1 border border-red-300 rounded hover:bg-red-50 transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </>
                      )}

                      {campaign.status === 'completed' && (
                        <span className="text-purple-600 text-sm font-medium">Completed</span>
                      )}

                      {campaign.status === 'cancelled' && (
                        <span className="text-red-600 text-sm font-medium">Cancelled</span>
                      )}

                      {campaign.status !== 'draft' && (
                        <button
                          onClick={() => toggleMessages(campaign.id)}
                          className={`text-sm px-3 py-1 rounded border transition-colors ${
                            expandedCampaignId === campaign.id
                              ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                              : 'text-indigo-600 hover:text-indigo-800 border-indigo-200 hover:bg-indigo-50'
                          }`}
                        >
                          {expandedCampaignId === campaign.id ? 'Hide Messages' : 'View Messages'}
                        </button>
                      )}
                    </div>
                  </div>

                  {expandedCampaignId === campaign.id && (
                    <div className="mt-4">
                      {messagesLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          <span className="ml-2 text-sm text-gray-600">Loading messages...</span>
                        </div>
                      ) : expandedMessages && expandedMessages.length > 0 ? (
                        <MessageSequenceDisplay
                          messages={expandedMessages}
                          title={`Messages for "${campaign.name}"`}
                          showBackground={true}
                        />
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">No messages found for this campaign.</p>
                      )}
                    </div>
                  )}
                </div>
              )))
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignsList;